import React from 'react';
import BaseModel from '../../model/BaseModel';
import { ServiceApi, WhereCondition, WhereLeaf, WhereTree } from '../../service/ServiceApi';
import OptionItem from './OptionItem';
import SelectSearchAsync from './SelectSearchAsync';
import { normalizeString } from '../../utils';

const buildSearchColumn = <T extends BaseModel>(column: keyof T | string, normalize?: boolean): keyof T | string => {
    if (!normalize) {
        return column;
    }

    if (typeof column === 'string') {
        return `${column}_normalized`;
    }

    return column;
};

interface SearchEntityProps<T, Key extends keyof T, Label extends keyof T> {
    name: string;
    label: string;
    valueField: Key;
    labelField: Label & string;
    entity: string;
    value: T[Key] | null;
    onChangeHandle: (name: string, value: T[Key] | null) => any;
    isDisabled?: boolean;
    required?: boolean;
    isClearable?: boolean;
    extraWhere?: WhereCondition<T>;
    extraOrWhere?: WhereCondition<T>;
    customLabel?: (i: T) => string;
    disableOptions?: (i: T) => boolean;
    style?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
    relations?: string[];
    filterResults?: (data: T[]) => T[];
    likeColumns?: (keyof T | string)[];
    conditions?: (WhereTree<T> | WhereLeaf<T>)[];
    useOldWhereApi?: boolean; // Se true, usa a API antiga de filtros (where e orWhere) ao invés da nova (whereTree)
    normalizeSearch?: boolean; // Se true, normaliza o texto de busca (remove acentuação)
    orderBy?: { [P in keyof T]?: 'ASC' | 'DESC' };
}

interface SearchEntityState<T> {
    defaultOptions: T[];
}

class SearchEntity<T extends BaseModel, Key extends keyof T, Label extends keyof T> extends React.Component<SearchEntityProps<T, Key, Label>, SearchEntityState<T>> {

    private api: ServiceApi<T>;

    constructor(props: SearchEntityProps<T, Key, Label>) {
        super(props);

        this.state = {
            defaultOptions: []
        };

        this.api = new ServiceApi<T>(props.entity);
        this.loadData = this.loadData.bind(this);
        this.loadOptions = this.loadOptions.bind(this);
        this.mapOptionItem = this.mapOptionItem.bind(this);
    }

    async componentDidMount() {
        const defaultOptions = await this.loadData('');
        this.setState({ defaultOptions });
    }

    async loadData(text: string): Promise<T[]> {
        const { extraWhere, labelField, value, extraOrWhere, relations, filterResults, likeColumns, conditions, useOldWhereApi, normalizeSearch, orderBy } = this.props;

        const orWhere: WhereCondition<T> = [];
        const where: WhereCondition<T> = [];
        const whereTree: WhereTree<T> = {
            operator: 'and',
            conditions: conditions || []
        };

        if (text && (!likeColumns || likeColumns.length === 0)) {
            const searchField = buildSearchColumn(labelField, normalizeSearch);
            whereTree.conditions.push({ field: searchField as keyof T, op: 'ilike', value: `%${text}%` });
            where.push([searchField as keyof T, 'ilike', `%${text}%`]);
        }

        if (extraWhere && Array.isArray(extraWhere)) {
            extraWhere.forEach(w => {
                whereTree.conditions.push({ field: w[0] as keyof T, op: w[1], value: w[2] });
                where.push([w[0] as keyof T, w[1], w[2]]);
            });
        }

        if (likeColumns && Array.isArray(likeColumns) && text) {
            likeColumns.forEach(col => {
                orWhere.push([col as keyof T, 'ilike', `%${text}%`]);
            });
        }

        if (extraOrWhere && Array.isArray(extraOrWhere)) {
            extraOrWhere.forEach(w => {
                orWhere.push([w[0] as keyof T, w[1], w[2]]);
            });
        }

        if (orWhere.length > 0) {
            whereTree.conditions.push({
                operator: 'or',
                conditions: orWhere.map(w => ({ field: w[0] as keyof T, op: w[1], value: w[2] }))
            });
        }

        const { data } = await this.api.getPagination({
            page: 1,
            per_page: 100,
            whereTree: useOldWhereApi ? undefined : whereTree,
            where: useOldWhereApi ? where : undefined,
            orWhere: useOldWhereApi ? orWhere : undefined,
            relations,
            order: orderBy || { [labelField]: 'ASC' }
        });

        var dados = data as T[];

        if (filterResults && typeof filterResults === 'function') {
            dados = filterResults(dados);
        }

        if (value && typeof value === 'number' && dados.filter(d => d.id == value).length === 0) {
            const result = await this.api.getById(value, { relations });
            if (result) {
                dados.unshift(result);
            }
        }

        return dados;
    }

    async loadOptions(text: string): Promise<OptionItem<T[Key]>[]> {
        const { normalizeSearch } = this.props;
        if (normalizeSearch) {
            text = normalizeString(text);
        }
        const data = await this.loadData(text);
        return data.map(this.mapOptionItem);
    }

    mapOptionItem(i: T): OptionItem<T[Key]> {
        const { valueField, disableOptions } = this.props;
        return {
            label: this.getLabel(i),
            value: i[valueField],
            disabled: disableOptions ? disableOptions(i) : false
        };
    }

    getLabel(i: T): string {
        const { customLabel, labelField } = this.props;
        if (customLabel) {
            return customLabel(i);
        }

        return String(i[labelField]);
    }

    render() {
        const { name, label, onChangeHandle, value, isDisabled, required, isClearable, style, inputStyle } = this.props;
        const { defaultOptions } = this.state;
        return (
            <SelectSearchAsync<T[Key]>
                name={name}
                label={label}
                loadOptions={this.loadOptions}
                onChangeHandle={(name, value) => onChangeHandle(name, value || null)}
                value={value}
                defaultOptions={defaultOptions.map(this.mapOptionItem)}
                margin="dense"
                fullWidth
                isDisabled={isDisabled}
                required={required}
                isClearable={isClearable}
                style={style}
                inputStyle={inputStyle}
            />
        );
    }

}

export default SearchEntity;